import { ProductFlowDiagram, type FlowStep } from "@/components/products/ProductFlowDiagram"
import { FAQAccordion, type FAQItem } from "@/components/public/FAQAccordion"
import { LandingFooter } from "@/components/public/LandingFooter"
import { PublicHeader } from "@/components/public/PublicHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { BreathingGradient } from "@/components/ui/living-brand"
import { SpotlightCard } from "@/components/ui/SpotlightCard"
import { Heading } from "@/components/ui/typography"
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { Page } from "@supabank/ui"
import { ArrowRight, Building2, CheckCircle2, Globe2, Send, Shield } from "lucide-react"
import { useEffect } from "react"
import { Link } from "react-router-dom"

const flowSteps: FlowStep[] = [
  {
    id: "create",
    title: "Get Bank Account Details",
    description: "Receive permanent bank account details (IBAN, routing number, or CLABE) in one click.",
    icon: <Building2 className="h-6 w-6" />,
  },
  {
    id: "receive",
    title: "Receive Fiat Payment",
    description: "Anyone can send fiat (USD, EUR, MXN) to your account via SEPA, ACH, Wire, or SPEI.",
    icon: <CheckCircle2 className="h-6 w-6" />,
  },
  {
    id: "convert",
    title: "Automatic Conversion",
    description: "We detect the deposit and automatically convert it to USDC, USDT, or other crypto at market rates.",
    icon: <Globe2 className="h-6 w-6" />,
  },
  {
    id: "deliver",
    title: "Crypto Delivery",
    description: "Converted crypto arrives in your specified wallet or Bridge Wallet—typically within minutes after conversion completes.",
    icon: <Send className="h-6 w-6" />,
  },
]

const supportedCurrencies = [
  { name: "USD", rails: ["ACH", "Wire"], description: "US Dollar via ACH or Wire transfer" },
  { name: "EUR", rails: ["SEPA", "Wire"], description: "Euro via SEPA or Wire transfer" },
  { name: "MXN", rails: ["SPEI", "Wire"], description: "Mexican Peso via SPEI or Wire transfer" },
  { name: "BRL", rails: ["PIX", "Wire"], description: "Brazilian Real via PIX or Wire (Beta, requires approval)" },
]

const supportedRails = [
  { name: "SEPA", region: "Eurozone (20+ countries)", description: "Single Euro Payments Area - instant or standard" },
  { name: "ACH", region: "United States", description: "Automated Clearing House - next-day settlement" },
  { name: "Wire", region: "Global", description: "SWIFT wire transfers - same-day or next-day" },
  { name: "SPEI", region: "Mexico", description: "Sistema de Pagos Electrónicos Interbancarios" },
  { name: "PIX", region: "Brazil (Beta)", description: "Instant payment system - requires Bridge approval" },
]

const useCases = [
  {
    title: "Fiat On-Ramp",
    description: "Receive fiat payments from customers and automatically convert them to crypto in your wallet.",
  },
  {
    title: "Multi-Currency Accounts",
    description: "Get separate account details for USD, EUR, and MXN. Each converts to crypto automatically.",
  },
  {
    title: "Business Deposits",
    description: "Accept bank transfers from clients. Funds automatically convert and arrive in your crypto wallet.",
  },
]

const faqItems: FAQItem[] = [
  {
    id: "what-is",
    q: "What is a Virtual Account?",
    a: "A permanent bank account (IBAN, routing number, or CLABE) that automatically converts incoming fiat payments to cryptocurrency and delivers them to your specified wallet. Once set up, it works forever—no expiration, no manual steps.",
  },
  {
    id: "how-long",
    q: "How long does conversion take?",
    a: "Most conversions complete within 2-5 minutes after the fiat deposit is confirmed by Bridge. Crypto delivery typically happens within minutes after conversion completes. Bank deposits may take 1-2 business days for SEPA/ACH, or same-day/next-day for Wire transfers depending on your bank.",
  },
  {
    id: "fees",
    q: "What are the fees?",
    a: "We charge a small conversion fee (typically 0.5-1%) on the exchange rate, plus a 2% developer fee. There are no setup fees, no monthly fees, and no hidden charges. You'll see the exact amount of crypto you'll receive before any deposit.",
  },
  {
    id: "supported",
    q: "Which currencies and payment rails are supported?",
    a: "We support USD (ACH, Wire), EUR (SEPA, Wire), MXN (SPEI, Wire), and BRL (PIX, Wire - Beta, requires approval). See the full list above.",
  },
  {
    id: "kyc",
    q: "Do I need to complete KYC?",
    a: "Yes, you need to complete KYC/KYB verification to create a Virtual Account. This is required by our compliance partner (Bridge API) and ensures all transactions are secure and compliant.",
  },
  {
    id: "security",
    q: "Is it secure?",
    a: "Yes. All accounts are created through Bridge API with institutional-grade security. We use KYC verification, automatic compliance checks, and all transactions are monitored. Your account details are permanent and managed by Bridge.",
  },
]

export default function VirtualAccountPage() {
  useEffect(() => {
    // SEO: Update page title and meta
    document.title = "Virtual Account - Receive Fiat, Get Crypto Automatically | Supabank"

    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Get permanent bank account details (IBAN, routing, CLABE) that automatically convert incoming fiat payments to USDC, USDT, or other cryptocurrencies and deliver them to your wallet.')
    }

    // Structured Data (JSON-LD)
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Virtual Account",
      "description": "Permanent bank account details that automatically convert incoming fiat payments to cryptocurrency and deliver them to your wallet",
      "brand": {
        "@type": "Brand",
        "name": "Supabank"
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      }
    }

    const howToData = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": "How to use Virtual Account",
      "description": "Step-by-step guide to using Supabank Virtual Account",
      "step": [
        {
          "@type": "HowToStep",
          "name": "Get Bank Account Details",
          "text": "Receive permanent bank account details (IBAN, routing number, or CLABE) in one click.",
          "position": 1
        },
        {
          "@type": "HowToStep",
          "name": "Receive Fiat Payment",
          "text": "Anyone can send fiat (USD, EUR, MXN) to your account via SEPA, ACH, Wire, or SPEI.",
          "position": 2
        },
        {
          "@type": "HowToStep",
          "name": "Automatic Conversion",
          "text": "We detect the deposit and automatically convert it to USDC, USDT, or other crypto at market rates.",
          "position": 3
        },
        {
          "@type": "HowToStep",
          "name": "Crypto Delivery",
          "text": "Converted crypto arrives in your specified wallet or Bridge Wallet—usually within minutes.",
          "position": 4
        }
      ]
    }

    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a
        }
      }))
    }

    // Remove existing structured data scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]')
    existingScripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '{}')
        if (data['@type'] === 'Product' || data['@type'] === 'HowTo' || data['@type'] === 'FAQPage') {
          script.remove()
        }
      } catch (e) {
        // Ignore parse errors
      }
    })

    // Add new structured data
    const script1 = document.createElement('script')
    script1.type = 'application/ld+json'
    script1.textContent = JSON.stringify(structuredData)
    document.head.appendChild(script1)

    const script2 = document.createElement('script')
    script2.type = 'application/ld+json'
    script2.textContent = JSON.stringify(howToData)
    document.head.appendChild(script2)

    const script3 = document.createElement('script')
    script3.type = 'application/ld+json'
    script3.textContent = JSON.stringify(faqData)
    document.head.appendChild(script3)

    return () => {
      script1.remove()
      script2.remove()
      script3.remove()
    }
  }, [])

  return (
    <Page data-ux-contract="canon" role="main" className="bg-background text-foreground">
      {/* Unified PublicHeader component */}
      <PublicHeader />

      <main className="space-y-0" id="main">
        {/* Hero Section */}
        <section className={cn(
          "relative overflow-hidden",
          "py-12 sm:py-16 md:py-20 lg:py-24",
          // CANON: Hero gradient background (lavenderGlow for fintech feel)
          semanticTokens.heroGradient.lavenderGlow.tailwind,
          // Dark mode variant
          "dark:bg-black dark:bg-[radial-gradient(ellipse_at_20%_50%,hsla(270,95%,75%,0.08)_0%,transparent_50%),radial-gradient(ellipse_at_80%_80%,hsla(217,91%,60%,0.06)_0%,transparent_50%),hsl(var(--background))]"
        )}>
          {/* Living Brand: Breathing Gradient (syncs with LED heartbeat) - DISABLED on dark theme */}
          <BreathingGradient
            variant="lavender"
            syncWithLed
            className="absolute inset-0 z-[1] pointer-events-none opacity-30 dark:hidden"
          />
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-6 sm:space-y-8 text-center">
              <div className={cn("inline-flex items-center justify-center rounded-full border px-4 py-2 text-sm font-medium", semanticTokens.border.default, semanticTokens.fg.muted, semanticTokens.radius.full)}>
                <Building2 className="mr-2 h-4 w-4" />
                Product
              </div>
              <h1 className={cn("font-display text-display-sm sm:text-display-md md:text-display-lg lg:text-display-lg", semanticTokens.fg.default)}>
                Receive Fiat. Get Crypto. Automatically.
              </h1>
              <p className={cn("mx-auto max-w-3xl text-lg sm:text-xl leading-relaxed", semanticTokens.fg.muted)}>
                Get your own bank account details (IBAN, routing number, or CLABE). Send fiat from any bank. We automatically convert it to USDC, USDT, or other cryptocurrencies and deliver it to your wallet.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button asChild size="lg" className="min-h-[44px] touch-manipulation">
                  <Link to="/waitlist">
                    Get Your Account Details
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="min-h-[44px] touch-manipulation">
                  <Link to="/how-payments-work">
                    Learn How It Works
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Flow Diagram */}
        <section className={cn(
          "relative overflow-hidden py-12 sm:py-16 md:py-20",
          semanticTokens.harmony.calm.bg
        )}>
          {/* Color Lab: Subtle Blue-Lavender gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 10% 90%, hsla(217, 91%, 60%, 0.10) 0%, transparent 45%),
                radial-gradient(ellipse at 90% 10%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%)
              `
            }}
          />
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  How It Works
                </Heading>
                <p className={cn("mx-auto max-w-2xl text-base sm:text-lg", semanticTokens.fg.muted)}>
                  Four simple steps. Fully automated. Zero manual work.
                </p>
              </div>
              <ProductFlowDiagram steps={flowSteps} direction="horizontal" />
            </div>
          </div>
        </section>

        {/* Supported Currencies & Rails */}
        <section className={cn("py-12 sm:py-16 md:py-20", semanticTokens.bg.surface)}>
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  Supported Currencies & Payment Rails
                </Heading>
                <p className={cn("mx-auto max-w-2xl text-base sm:text-lg", semanticTokens.fg.muted)}>
                  Receive fiat payments from anywhere in the world via local payment rails.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {supportedCurrencies.map((currency) => (
                  <SpotlightCard
                    key={currency.name}
                    className={cn(
                      "p-5 sm:p-6 border",
                      semanticTokens.bg.surface,
                      semanticTokens.border.default
                    )}
                  >
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                          {currency.name}
                        </h3>
                        <div className="flex gap-2">
                          {currency.rails.map((rail) => (
                            <Badge key={rail} variant="outline" className="text-xs">
                              {rail}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                        {currency.description}
                      </p>
                    </div>
                  </SpotlightCard>
                ))}
              </div>

              <div className="mt-8 sm:mt-10">
                <h3 className={cn("text-lg sm:text-xl font-semibold mb-4 text-center", semanticTokens.fg.default)}>
                  Payment Rails Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {supportedRails.map((rail) => (
                    <SpotlightCard
                      key={rail.name}
                      className={cn(
                        "p-5 sm:p-6 border",
                        semanticTokens.bg.surface,
                        semanticTokens.border.default
                      )}
                    >
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className={cn("text-base font-bold", semanticTokens.fg.default)}>
                            {rail.name}
                          </h4>
                          {rail.name === "PIX" && (
                            <Badge variant="secondary" className="text-xs">
                              Beta
                            </Badge>
                          )}
                        </div>
                        <p className={cn("text-xs font-medium", semanticTokens.fg.muted)}>
                          {rail.region}
                        </p>
                        <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                          {rail.description}
                        </p>
                      </div>
                    </SpotlightCard>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className={cn(
          "relative overflow-hidden py-12 sm:py-16 md:py-20",
          semanticTokens.harmony.calm.bg
        )}>
          {/* Color Lab: Subtle Blue-Lavender gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 10% 90%, hsla(217, 91%, 60%, 0.10) 0%, transparent 45%),
                radial-gradient(ellipse at 90% 10%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%)
              `
            }}
          />
          <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  Use Cases
                </Heading>
                <p className={cn("mx-auto max-w-2xl text-base sm:text-lg", semanticTokens.fg.muted)}>
                  Perfect for businesses and individuals who receive fiat payments and want automatic crypto conversion.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                {useCases.map((useCase) => (
                  <SpotlightCard
                    key={useCase.title}
                    className={cn(
                      "p-6 sm:p-8 border",
                      semanticTokens.bg.surface,
                      semanticTokens.border.default
                    )}
                  >
                    <div className="space-y-3">
                      <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                        {useCase.title}
                      </h3>
                      <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                        {useCase.description}
                      </p>
                    </div>
                  </SpotlightCard>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Security & Compliance */}
        <section className={cn("py-12 sm:py-16 md:py-20", semanticTokens.bg.surface)}>
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="space-y-6 sm:space-y-8">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  Security & Compliance
                </Heading>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                <SpotlightCard className={cn("p-6 sm:p-8 border", semanticTokens.bg.surface, semanticTokens.border.default)}>
                  <div className="flex items-start gap-4">
                    <div className={cn("rounded-lg p-3", semanticTokens.bg.muted)}>
                      <Shield className={cn("h-6 w-6", semanticTokens.fg.default)} />
                    </div>
                    <div className="space-y-2 flex-1">
                      <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                        Institutional Security
                      </h3>
                      <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                        All accounts are created through Bridge API with institutional-grade security. Your account details are permanent and managed by Bridge.
                      </p>
                    </div>
                  </div>
                </SpotlightCard>

                <SpotlightCard className={cn("p-6 sm:p-8 border", semanticTokens.bg.surface, semanticTokens.border.default)}>
                  <div className="flex items-start gap-4">
                    <div className={cn("rounded-lg p-3", semanticTokens.bg.muted)}>
                      <CheckCircle2 className={cn("h-6 w-6", semanticTokens.fg.default)} />
                    </div>
                    <div className="space-y-2 flex-1">
                      <h3 className={cn("text-lg font-bold", semanticTokens.fg.default)}>
                        Automatic Compliance
                      </h3>
                      <p className={cn("text-sm leading-relaxed", semanticTokens.fg.muted)}>
                        KYC verification, automatic compliance checks, and transaction monitoring. All transactions are screened before funds move.
                      </p>
                    </div>
                  </div>
                </SpotlightCard>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className={cn(
          "relative overflow-hidden py-12 sm:py-16 md:py-20",
          semanticTokens.harmony.calm.bg
        )}>
          {/* Color Lab: Subtle Lavender gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `
                radial-gradient(ellipse at 80% 20%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%),
                radial-gradient(ellipse at 20% 80%, hsla(217, 91%, 60%, 0.06) 0%, transparent 45%)
              `
            }}
          />
          <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6">
            <div className="space-y-8 sm:space-y-10">
              <div className="text-center space-y-3">
                <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
                  Frequently Asked Questions
                </Heading>
              </div>
              <FAQAccordion items={faqItems} />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={cn("py-12 sm:py-16 md:py-20", semanticTokens.bg.surface)}>
          <div className="mx-auto max-w-4xl px-4 sm:px-6 text-center space-y-6">
            <Heading level={2} className={cn("sm:text-display-sm md:text-display-md", semanticTokens.fg.default)}>
              Ready to Get Started?
            </Heading>
            <p className={cn("text-base sm:text-lg", semanticTokens.fg.muted)}>
              Get your permanent bank account details and start receiving automatic crypto conversions today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="min-h-[44px] touch-manipulation">
                <Link to="/waitlist">
                  Get Your Account Details
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="min-h-[44px] touch-manipulation">
                <Link to="/products">
                  View All Products
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />
    </Page>
  )
}

